/**
 * Generic REST resource + its React Query hooks.
 *
 * Every domain in this app is the same five operations over a different path, so they
 * are written once here. A domain module then reads:
 *
 *   export const policies = createResource<PolicyDto, PolicyListQuery>('policies');
 *
 * and gets typed list/detail/create/update/delete hooks with consistent cache keys and
 * invalidation. Domain-specific calls (renew, reconcile, …) are added alongside, so the
 * generic path stays generic instead of growing flags.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { PaginatedResponse } from '@ias/shared';

import { getList, getOne, post, put, remove } from './client';
import type { ApiRequestError } from './client';

/** Cache-key factory. Keeping every key derived from one place makes invalidation exact. */
export const queryKeys = {
  resource: (name: string) => [name] as const,
  list: (name: string, params?: unknown) => [name, 'list', params ?? {}] as const,
  detail: (name: string, id: string) => [name, 'detail', id] as const,
  sub: (name: string, id: string, child: string, params?: unknown) =>
    [name, 'detail', id, child, params ?? {}] as const,
  custom: (name: string, segment: string, params?: unknown) => [name, segment, params ?? {}] as const,
};

export interface Resource<TDto, TQuery> {
  name: string;
  path: string;
  useList: (
    params?: TQuery,
    options?: { enabled?: boolean; placeholderData?: unknown },
  ) => ReturnType<typeof useQuery<PaginatedResponse<TDto>, ApiRequestError>>;
  useDetail: (
    id: string | undefined,
    options?: Partial<UseQueryOptions<TDto, ApiRequestError>>,
  ) => ReturnType<typeof useQuery<TDto, ApiRequestError>>;
  useCreate: <TInput>(
    options?: UseMutationOptions<TDto, ApiRequestError, TInput>,
  ) => ReturnType<typeof useMutation<TDto, ApiRequestError, TInput>>;
  useUpdate: <TInput>(
    options?: UseMutationOptions<TDto, ApiRequestError, { id: string; input: TInput }>,
  ) => ReturnType<typeof useMutation<TDto, ApiRequestError, { id: string; input: TInput }>>;
  useRemove: (
    options?: UseMutationOptions<void, ApiRequestError, string>,
  ) => ReturnType<typeof useMutation<void, ApiRequestError, string>>;
  /** Invalidates every query belonging to this resource. */
  useInvalidate: () => () => Promise<void>;
}

export function createResource<TDto, TQuery = Record<string, unknown>>(
  name: string,
  path: string = name,
): Resource<TDto, TQuery> {
  return {
    name,
    path,

    useList(params, options) {
      return useQuery<PaginatedResponse<TDto>, ApiRequestError>({
        queryKey: queryKeys.list(name, params),
        queryFn: () => getList<TDto>(`/${path}`, params),
        enabled: options?.enabled ?? true,
        // Keeps the previous page visible while the next one loads, so paging and
        // filtering never blank the table out.
        placeholderData: (previous) => previous,
        staleTime: 30_000,
      });
    },

    useDetail(id, options) {
      return useQuery<TDto, ApiRequestError>({
        queryKey: queryKeys.detail(name, id ?? ''),
        queryFn: () => getOne<TDto>(`/${path}/${id}`),
        enabled: Boolean(id),
        ...options,
      });
    },

    useCreate(options) {
      const client = useQueryClient();
      return useMutation({
        mutationFn: (input) => post<TDto>(`/${path}`, input),
        ...options,
        onSuccess: async (...args) => {
          await client.invalidateQueries({ queryKey: queryKeys.resource(name) });
          await (options?.onSuccess as ((...a: unknown[]) => unknown) | undefined)?.(...args);
        },
      });
    },

    useUpdate(options) {
      const client = useQueryClient();
      return useMutation({
        mutationFn: ({ id, input }) => put<TDto>(`/${path}/${id}`, input),
        ...options,
        onSuccess: async (...args) => {
          await client.invalidateQueries({ queryKey: queryKeys.resource(name) });
          await (options?.onSuccess as ((...a: unknown[]) => unknown) | undefined)?.(...args);
        },
      });
    },

    useRemove(options) {
      const client = useQueryClient();
      return useMutation({
        mutationFn: (id) => remove(`/${path}/${id}`),
        ...options,
        onSuccess: async (...args) => {
          await client.invalidateQueries({ queryKey: queryKeys.resource(name) });
          await (options?.onSuccess as ((...a: unknown[]) => unknown) | undefined)?.(...args);
        },
      });
    },

    useInvalidate() {
      const client = useQueryClient();
      return () => client.invalidateQueries({ queryKey: queryKeys.resource(name) });
    },
  };
}
